import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import PageShell from "../../components/layout/PageShell";
import {
    getClientBusinessSettings,
    saveClientBusinessSettings
} from "../../services/crmService";

import "../Setting.css";

function SettingHelp({ title, children }) {
    return (
        <div className="client-setting-help">
            <strong>{title}</strong>
            <span>{children}</span>
        </div>
    );
}

function ToggleSetting({ checked, onChange, title, description }) {
    return (
        <label className="client-toggle-card">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            <div>
                <strong>{title}</strong>
                <span>{description}</span>
            </div>
        </label>
    );
}

export default function ClientBusinessSettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        businessName: "",
        logoUrl: "",
        primaryColor: "#2563eb",
        toneOfVoice: "professional",

        contactEmail: "",
        phone: "",
        whatsAppNumber: "",
        address: "",

        enableContactForm: true,
        enableBookingModal: true,
        enableWhatsAppButton: true,
        enableCallButton: true
    });

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);

                const data = await getClientBusinessSettings();

                if (!mounted) return;

                setForm(prev => ({
                    ...prev,
                    ...(data || {})
                }));
            } catch (error) {
                console.error(error);
                toast.error("Failed to load website settings.");
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, []);

    const update = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            toast.loading("Saving website settings...", { id: "client-business-settings" });

            const saved = await saveClientBusinessSettings(form);

            setForm(prev => ({
                ...prev,
                ...(saved || {})
            }));

            toast.success("Website settings saved.", { id: "client-business-settings" });
        } catch (error) {
            console.error(error);
            toast.error(
                error?.response?.data || "Failed to save website settings.",
                { id: "client-business-settings" }
            );
        } finally {
            setSaving(false);
        }
    };

    const enabledActions = useMemo(() => {
        return [
            form.enableContactForm ? "Contact form" : null,
            form.enableBookingModal ? "Booking popup" : null,
            form.enableWhatsAppButton ? "WhatsApp button" : null,
            form.enableCallButton ? "Call button" : null
        ].filter(Boolean);
    }, [form]);

    if (loading) {
        return (
            <PageShell title="Business & Website Settings">
                <div className="project-loading-card">Loading website settings...</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="Business & Website Settings">
            <div className="settings-shell client-settings-shell">
                <section className="client-settings-hero">
                    <div>
                        <span className="project-kicker">Website Settings</span>
                        <h1>Business & Website</h1>
                        <p>
                            Control how your business appears on generated websites and which contact actions visitors can use.
                        </p>

                        <div className="client-settings-summary">
                            <span>{form.businessName || "Business name not set"}</span>
                            <span>{form.contactEmail || "Contact email not set"}</span>
                            <span>{enabledActions.length ? `${enabledActions.length} website actions enabled` : "No website actions enabled"}</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="settings-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </section>

                <section className="client-settings-layout">
                    <div className="client-settings-main">
                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Brand shown on website</span>
                            <h2>Business Identity</h2>
                            <p className="settings-muted">
                                These details help Nexus keep your generated website, CRM, and customer messages consistent.
                            </p>

                            <div className="settings-field-grid">
                                <label>
                                    <span>Business Name</span>
                                    <input
                                        value={form.businessName || ""}
                                        onChange={(e) => update("businessName", e.target.value)}
                                        placeholder="Example: Shriansh Football Academy"
                                    />
                                    <small>The public name shown on your website header, footer, and forms.</small>
                                </label>

                                <label>
                                    <span>Logo Image URL</span>
                                    <input
                                        value={form.logoUrl || ""}
                                        onChange={(e) => update("logoUrl", e.target.value)}
                                        placeholder="https://..."
                                    />
                                    <small>Optional. Add a logo image link if you want it used in the website/CRM.</small>
                                </label>

                                <label>
                                    <span>Main Brand Color</span>
                                    <input
                                        value={form.primaryColor || ""}
                                        onChange={(e) => update("primaryColor", e.target.value)}
                                        placeholder="#2563eb"
                                    />
                                    <small>Used for buttons and highlights where the website supports brand colors.</small>
                                </label>

                                <label>
                                    <span>Writing Style</span>
                                    <select
                                        value={form.toneOfVoice || "professional"}
                                        onChange={(e) => update("toneOfVoice", e.target.value)}
                                    >
                                        <option value="professional">Professional</option>
                                        <option value="friendly">Friendly</option>
                                        <option value="premium">Premium</option>
                                        <option value="bold">Bold</option>
                                    </select>
                                    <small>Guides how AI writes future suggestions and improvements.</small>
                                </label>
                            </div>
                        </div>

                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Where enquiries should go</span>
                            <h2>Public Contact Details</h2>
                            <p className="settings-muted">
                                These details can appear on contact pages, enquiry forms, WhatsApp links, call buttons, and footers.
                            </p>

                            <div className="settings-field-grid">
                                <label>
                                    <span>Enquiry Email</span>
                                    <input
                                        value={form.contactEmail || ""}
                                        onChange={(e) => update("contactEmail", e.target.value)}
                                        placeholder="hello@example.com"
                                    />
                                    <small>Used as the main email visitors can contact.</small>
                                </label>

                                <label>
                                    <span>Phone Number</span>
                                    <input
                                        value={form.phone || ""}
                                        onChange={(e) => update("phone", e.target.value)}
                                        placeholder="+91..."
                                    />
                                    <small>Used for call buttons and public contact sections.</small>
                                </label>

                                <label>
                                    <span>WhatsApp Number</span>
                                    <input
                                        value={form.whatsAppNumber || ""}
                                        onChange={(e) => update("whatsAppNumber", e.target.value)}
                                        placeholder="+91..."
                                    />
                                    <small>Used when WhatsApp quick action is enabled.</small>
                                </label>

                                <label>
                                    <span>Address / Service Area</span>
                                    <input
                                        value={form.address || ""}
                                        onChange={(e) => update("address", e.target.value)}
                                        placeholder="Area, city, address, or service location"
                                    />
                                    <small>Shown where your website needs location or service area text.</small>
                                </label>
                            </div>
                        </div>

                        <div className="project-panel client-setting-panel">
                            <span className="project-kicker">Visitor actions</span>
                            <h2>How visitors can contact you</h2>
                            <p className="settings-muted">
                                Choose which actions should be available on your generated website. Turning these off hides or disables the related website action where supported.
                            </p>

                            <div className="client-toggle-grid">
                                <ToggleSetting
                                    checked={!!form.enableContactForm}
                                    onChange={(value) => update("enableContactForm", value)}
                                    title="Show enquiry form"
                                    description="Visitors can submit their name, email/phone, and message from the contact page."
                                />

                                <ToggleSetting
                                    checked={!!form.enableBookingModal}
                                    onChange={(value) => update("enableBookingModal", value)}
                                    title="Show booking popup"
                                    description="Visitors can open a quick booking/enquiry popup from website buttons."
                                />

                                <ToggleSetting
                                    checked={!!form.enableWhatsAppButton}
                                    onChange={(value) => update("enableWhatsAppButton", value)}
                                    title="Show WhatsApp button"
                                    description="Visitors can start a WhatsApp conversation using your WhatsApp number."
                                />

                                <ToggleSetting
                                    checked={!!form.enableCallButton}
                                    onChange={(value) => update("enableCallButton", value)}
                                    title="Show call button"
                                    description="Visitors can tap to call your business on supported devices."
                                />
                            </div>
                        </div>
                    </div>

                    <aside className="client-settings-side">
                        <SettingHelp title="What changes after saving?">
                            New settings are stored for your account. Website actions and contact details are used when pages are rendered or redeployed.
                        </SettingHelp>

                        <SettingHelp title="Will this instantly change my live site?">
                            Some settings are used immediately in CRM. For the live website, use Save & Publish or Update Live Site on your project after changing key website details.
                        </SettingHelp>

                        <SettingHelp title="Recommended setup">
                            Add enquiry email, phone, WhatsApp number, and keep at least one visitor action enabled so leads can reach you easily.
                        </SettingHelp>
                    </aside>
                </section>

                <div className="settings-sticky-save">
                    <span>Business & Website settings</span>

                    <button
                        type="button"
                        className="settings-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </div>
            </div>
        </PageShell>
    );
}